import * as moment from 'moment';

import { type Option } from '~/components/SmartSelect';
import { countries } from '~/constants/countries';
import { currencies, type Currency } from '~/constants/currency';
import { type WeightUnit } from '~/constants/weight';

import { type Country } from './Address';

type JSONLineItem = {
  app_id: string;
  country_of_origin: Maybe<string>;
  currency: Maybe<string>;
  hs_code: Maybe<string>;
  id: number;
  is_fulfilled: boolean;
  manufacture_country: Maybe<string>; // deprecated
  // TODO: Revisit
  max_delivery_time: Maybe<string>;
  max_ship_time: Maybe<string>;
  object_id: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parcel?: any;
  product: string;
  quantity: number;
  sku: string;
  title: string;
  total_amount: string;
  total_weight: string;
  unit_amount: string;
  unit_weight: string;
  variant: string;
  variant_title: string;
  weight_unit: Maybe<string>;
};

type LineItem = {
  appId: string;
  countryOfOrigin: Maybe<Country>;
  currency: Currency;
  hsCode: Maybe<string>;
  id: number;
  isFulfilled: boolean;
  manufactureCountry: Maybe<string>; // deprecated
  // TODO: Revisit
  maxDeliveryTime: Maybe<moment.Moment>;
  maxShipTime: Maybe<moment.Moment>;
  objectId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parcel?: any;
  product: string;
  quantity: number;
  sku: string;
  title: string;
  totalAmount: string;
  totalWeight: string;
  unitAmount: string;
  unitWeight: string;
  variant: string;
  variantTitle: string;
  weightUnit: Maybe<WeightUnit>;
};

type LineItemPayload = {
  countryOfOrigin: Country;
  currency: Option;
  quantity: string;
  sku: string;
  title: string;
  unitAmount: string;
  unitWeight: string;
  weightUnit: Option;
};

type JSONLineItemPayload = {
  country_of_origin: string;
  currency: string;
  quantity: number;
  sku: string;
  title: string;
  unit_amount: string;
  unit_weight: string;
  weight_unit: string;
};

export type WebLineItem = {
  country_of_origin: string;
  currency: string;
  is_fulfilled: boolean;
  object_id: string;
  quantity: number;
  sku: string;
  title: string;
  total_amount: string;
  total_weight: string;
  unit_amount: string;
  unit_weight: string;
  variant: string;
  variant_title: string;
  weight_unit: WeightUnit;
};

export type WebShipmentLineItemsInner = {
  object_id: string;
  quantity: number;
};

const fromJSON = (jsonLineItem: JSONLineItem): LineItem => ({
  appId: jsonLineItem.app_id,
  countryOfOrigin: jsonLineItem.country_of_origin
    ? countries[jsonLineItem.country_of_origin.toUpperCase()]
    : null,
  currency: jsonLineItem.currency
    ? Object.values(currencies).find(
        (currency) => currency.iso === jsonLineItem.currency!.toUpperCase(),
      ) || currencies.USD
    : currencies.USD,
  hsCode: jsonLineItem.hs_code,
  id: jsonLineItem.id,
  isFulfilled: jsonLineItem.is_fulfilled,
  manufactureCountry: jsonLineItem.manufacture_country,
  maxDeliveryTime: jsonLineItem.max_delivery_time ? moment(jsonLineItem.max_delivery_time) : null,
  maxShipTime: jsonLineItem.max_ship_time ? moment(jsonLineItem.max_ship_time) : null,
  objectId: jsonLineItem.object_id,
  parcel: jsonLineItem.parcel,
  product: jsonLineItem.product,
  quantity: jsonLineItem.quantity,
  sku: jsonLineItem.sku,
  title: jsonLineItem.title,
  totalAmount: jsonLineItem.total_amount,
  totalWeight: jsonLineItem.total_weight,
  unitAmount: jsonLineItem.unit_amount,
  unitWeight: jsonLineItem.unit_weight,
  variant: jsonLineItem.variant,
  variantTitle: jsonLineItem.variant_title,
  weightUnit: jsonLineItem.weight_unit ? <WeightUnit>jsonLineItem.weight_unit : null,
});

export const fromWebLineItem = (webLineItem: WebLineItem): LineItem => {
  const currency =
    Object.values(currencies).find((currency) => currency.iso === webLineItem.currency) ||
    currencies.USD;
  const countryOfOrigin = countries[webLineItem.country_of_origin.toUpperCase()];
  return {
    appId: '',
    countryOfOrigin,
    currency,
    hsCode: undefined,
    //nulls
    id: -1,
    isFulfilled: webLineItem.is_fulfilled,
    manufactureCountry: countryOfOrigin?.iso2,
    maxDeliveryTime: undefined,
    maxShipTime: undefined,
    objectId: webLineItem.object_id,
    parcel: undefined,
    product: '',
    quantity: webLineItem.quantity,
    sku: webLineItem.sku,
    title: webLineItem.title,

    totalAmount: webLineItem.total_amount,
    totalWeight: webLineItem.total_weight,
    unitAmount: webLineItem.unit_amount,
    unitWeight: webLineItem.unit_weight,
    variant: webLineItem.variant,
    variantTitle: webLineItem.variant_title,
    weightUnit: webLineItem.weight_unit,
  };
};

const lineItemHasWeight = (lineItem: LineItem): boolean =>
  lineItem.weightUnit !== null &&
  lineItem.quantity !== 0 &&
  !!lineItem.totalWeight &&
  parseFloat(lineItem.totalWeight) !== 0;

const isLineItemValid = (lineItem: LineItem): boolean =>
  [
    lineItem.quantity,
    lineItem.title,
    lineItem.weightUnit,
    lineItem.unitAmount,
    lineItem.unitWeight,
  ].every(Boolean);

const isCustomsReady = (lineItem: LineItem): boolean =>
  isLineItemValid(lineItem) && !!lineItem.countryOfOrigin;

const payloadToJSON = (lineItemPayload: LineItemPayload): JSONLineItemPayload => ({
  country_of_origin: lineItemPayload.countryOfOrigin.iso2,
  currency: lineItemPayload.currency.value as string,
  quantity: Number(lineItemPayload.quantity),
  sku: lineItemPayload.sku,
  title: lineItemPayload.title,
  unit_amount: lineItemPayload.unitAmount,
  unit_weight: lineItemPayload.unitWeight,
  weight_unit: lineItemPayload.weightUnit.value as string,
});

const getTotalValue = (total: number, lineItem: LineItem): number => {
  const parsedValue = parseFloat(lineItem.totalAmount);
  return !isNaN(parsedValue) ? total + parsedValue : total;
};

const getTotalWeight = (total: number, lineItem: LineItem): number => {
  const parsedWeight = parseFloat(lineItem.totalWeight);
  return !isNaN(parsedWeight) ? total + parsedWeight : total;
};

const getLineItemsTotalValue = (lineItems: LineItem[]): number =>
  lineItems.reduce(getTotalValue, 0.0);

const getLineItemsTotalWeight = (linteItems: LineItem[]): number =>
  linteItems.reduce(getTotalWeight, 0.0);

const totalQuantity = (lineItems: Pick<LineItem, 'quantity'>[]): number =>
  lineItems.reduce((tot, li) => tot + li.quantity, 0);

const sort = <T extends { quantity: number; title: string }>(lineItems: T[]): T[] =>
  lineItems.sort((a, b) => {
    if (a.quantity === b.quantity) return a.title.localeCompare(b.title);

    return a.quantity > b.quantity ? -1 : 1;
  });

export default LineItem;
export {
  JSONLineItem,
  JSONLineItemPayload,
  LineItemPayload,
  fromJSON,
  getLineItemsTotalValue,
  getLineItemsTotalWeight,
  getTotalValue,
  getTotalWeight,
  isCustomsReady,
  isLineItemValid,
  lineItemHasWeight,
  payloadToJSON,
  sort,
  totalQuantity,
};
